/// <reference types="vite/client" />

import { useCallback, useEffect, useRef, useState } from "react";

export interface RecentVerification {
  id: string;
  agentId: string;
  agentName: string;
  outputType: string;
  verdict: "PASS" | "FAIL" | "CONDITIONAL" | "BLOCK" | null;
  status: string;
  iteration: number;
  maxIterations: number;
  timestamp: string;
  feedback: string[];
}

interface UseRecentVerificationsOptions {
  /** Auto-poll interval in ms. Default 10s. Set to 0 to disable polling. */
  pollMs?: number;
  /** Filter by agent slug. */
  agent?: string;
  /** Max entries to fetch. Default 20, max 100 server-side. */
  limit?: number;
}

interface UseRecentVerificationsReturn {
  entries: RecentVerification[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const DEFAULT_POLL_MS = 10_000;

/**
 * Live-poll the Worker `/api/verifications/recent` endpoint and return
 * the latest verification log entries for the dashboard.
 *
 * Polling stops on unmount. The first fetch fires on mount.
 */
export function useRecentVerifications(
  options: UseRecentVerificationsOptions = {},
): UseRecentVerificationsReturn {
  const { pollMs = DEFAULT_POLL_MS, agent, limit = 20 } = options;
  const [entries, setEntries] = useState<RecentVerification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const params = new URLSearchParams();
    if (limit) params.set("limit", String(limit));
    if (agent) params.set("agent", agent);
    const qs = params.toString();
    const url = `/api/verifications/recent${qs ? `?${qs}` : ""}`;

    try {
      const res = await fetch(url, { signal: ctrl.signal });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${body ? ` — ${body.slice(0, 200)}` : ""}`);
      }
      const json = (await res.json()) as RecentVerification[];
      setEntries(json);
      setError(null);
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      setError((err as Error).message);
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(
          "[useRecentVerifications] /api/verifications/recent fetch failed. Worker running?",
          err,
        );
      }
    } finally {
      setLoading(false);
    }
  }, [agent, limit]);

  useEffect(() => {
    void load();
    if (!pollMs) return;
    const id = setInterval(() => void load(), pollMs);
    return () => {
      clearInterval(id);
      abortRef.current?.abort();
    };
  }, [load, pollMs]);

  return { entries, loading, error, refresh: () => void load() };
}
